import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Image,
  Video,
  FileText,
  Mic,
  Map,
  Tags,
  Square,
  Scissors,
  Tag,
  MapPin,
  Link2,
  Smile,
  Upload,
  MessageSquare,
  Users,
  CheckCircle,
  Download,
  UserCheck,
  Search,
  FileCheck,
  Target,
  Lock,
  Zap,
  Car,
  Stethoscope,
  ShoppingCart,
  Landmark,
  Factory,
  Smartphone,
  Wheat,
  Film,
  Check,
  ChevronRight,
  Lightbulb,
  ArrowRight,
  X,
  Loader,
  Database
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= SAMPLE REQUEST MODAL =============
const SampleRequestModal = ({ isOpen, onClose, selectedPlan = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    dataType: '',
    labelType: '',
    quantity: '',
    timeline: '',
    plan: selectedPlan || '',
    description: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  // Update plan when selectedPlan changes
  React.useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({ ...prev, plan: selectedPlan }));
    }
  }, [selectedPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.dataType || !formData.description) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'Data Labelling Services',
          message: `Data Labelling Sample Request\n\nCompany: ${formData.company || 'Not specified'}\nData Type: ${formData.dataType}\nLabel Type: ${formData.labelType || 'Not specified'}\nEstimated Quantity: ${formData.quantity || 'Not specified'}\nTimeline: ${formData.timeline || 'Not specified'}\nPricing Interest: ${formData.plan || 'Not specified'}\n\nProject Description:\n${formData.description}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Request submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', company: '', dataType: '', labelType: '', quantity: '', timeline: '', plan: '', description: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Sample request error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', company: '', dataType: '', labelType: '', quantity: '', timeline: '', plan: selectedPlan || '', description: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Sample Request Received!</h3>
            <p className="text-gray-400 mb-6">
              We'll prepare a free labeled sample of your data and send it within 24-48 hours. Check your email!
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Database size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">Free Sample</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Get Free Sample</h2>
            <p className="text-teal-400 mb-6">See our labeling quality firsthand</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Company Name</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your company name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Data Type *</label>
                  <select
                    value={formData.dataType}
                    onChange={(e) => handleChange('dataType', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Images" className="bg-slate-950">Images</option>
                    <option value="Video" className="bg-slate-950">Video</option>
                    <option value="Text" className="bg-slate-950">Text</option>
                    <option value="Audio" className="bg-slate-950">Audio</option>
                    <option value="Geospatial" className="bg-slate-950">Geospatial</option>
                    <option value="Products" className="bg-slate-950">Products</option>
                    <option value="Mixed" className="bg-slate-950">Mixed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Label Type</label>
                  <select
                    value={formData.labelType}
                    onChange={(e) => handleChange('labelType', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Bounding Boxes" className="bg-slate-950">Bounding Boxes</option>
                    <option value="Segmentation" className="bg-slate-950">Segmentation</option>
                    <option value="Classification" className="bg-slate-950">Classification</option>
                    <option value="Keypoints" className="bg-slate-950">Keypoints</option>
                    <option value="Relationships" className="bg-slate-950">Relationships</option>
                    <option value="Sentiment" className="bg-slate-950">Sentiment</option>
                    <option value="Not Sure" className="bg-slate-950">Not Sure</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Est. Quantity</label>
                  <select
                    value={formData.quantity}
                    onChange={(e) => handleChange('quantity', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="100-1,000" className="bg-slate-950">100-1,000</option>
                    <option value="1,000-10,000" className="bg-slate-950">1,000-10,000</option>
                    <option value="10,000-100,000" className="bg-slate-950">10,000-100,000</option>
                    <option value="100,000+" className="bg-slate-950">100,000+</option>
                    <option value="Millions" className="bg-slate-950">Millions</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Timeline</label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => handleChange('timeline', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Rush (2-3 days)" className="bg-slate-950">Rush (2-3 days)</option>
                    <option value="Standard (5-7 days)" className="bg-slate-950">Standard (5-7 days)</option>
                    <option value="Flexible" className="bg-slate-950">Flexible</option>
                    <option value="Ongoing" className="bg-slate-950">Ongoing</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Pricing Interest</label>
                <select
                  value={formData.plan}
                  onChange={(e) => handleChange('plan', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select plan</option>
                  <option value="Basic Labels ($0.10/item)" className="bg-slate-950">Basic Labels ($0.10/item)</option>
                  <option value="Advanced Labels ($0.50/item)" className="bg-slate-950">Advanced Labels ($0.50/item)</option>
                  <option value="Enterprise Volume (Custom)" className="bg-slate-950">Enterprise Volume (Custom)</option>
                  <option value="Not sure yet" className="bg-slate-950">Not sure yet</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">What needs labeling? *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Describe your data and what labels you need (e.g., 'I have car images and need bounding boxes around each vehicle')"
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Get Free Sample
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onGetSample }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-white transition">Home</Link>
            <Link to="/services" className="text-sm text-gray-300 hover:text-white transition">Services</Link>
            <a href="#process" className="text-sm text-gray-300 hover:text-white transition">Process</a>
            <a href="#pricing" className="text-sm text-gray-300 hover:text-white transition">Pricing</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onGetSample}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Get Free Sample
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onGetSample }) => {
  const stats = [
    { value: '10M+', label: 'Items Labeled' },
    { value: '99.5%', label: 'Accuracy Rate' },
    { value: '50+', label: 'Languages Supported' },
    { value: '24/7', label: 'Work Around Clock' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <GradientOrbs />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.2),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badges */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {['Teaching AI', 'High Quality', 'Accurate Labels'].map((badge, i) => (
            <span
              key={i}
              className="text-xs font-semibold text-slate-900 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400"
            >
              {badge}
            </span>
          ))}
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="text-white">Data Labelling</span>
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Services
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-4 font-light tracking-wide"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          We Teach Smart Technology to Recognize Things
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-base md:text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Before AI can work, it needs to learn—just like a child learning to recognize cats, dogs, 
          and cars. We label your data (add tags and descriptions) so AI can understand what it's 
          looking at. Think of us as teachers creating flashcards for very smart computers. Without 
          us, AI can't tell the difference between a stop sign and a pizza!
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onGetSample}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-2"
          >
            Get Free Sample <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('industries').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-400/40"
          >
            See Our Work
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative group"
              whileHover={{ y: -5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= EXPLAINER SECTION =============
const ExplainerSection = () => {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Understanding Labeling
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            What Is Data Labeling?
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Let's break down what we do in the simplest way possible. No technical jargon—
            just a straightforward explanation anyone can understand.
          </p>
        </motion.div>

        <motion.div
          className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />
          
          <div className="flex justify-center -mt-16 mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <Lightbulb className="text-white" size={32} />
            </div>
          </div>

          <h3 className="text-2xl font-bold text-teal-400 text-center mb-6">
            Think of It Like Teaching a Toddler
          </h3>
          <p className="text-gray-300 text-lg leading-relaxed text-center max-w-3xl mx-auto">
            Imagine teaching a 2-year-old to recognize animals. You show them pictures and say 
            "this is a dog," "this is a cat," "this is a bird." After seeing hundreds of examples 
            with you pointing and naming, they learn to identify animals on their own. That's 
            exactly what we do for AI! We look at thousands of images, videos, or text and add 
            labels (tags) explaining what's in them. The AI studies our labeled examples and learns 
            to recognize things by itself. No labels = confused AI that can't do anything useful.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ============= SERVICE CARD =============
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-teal-400/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

        <div className="relative z-10 flex flex-col h-full">
          <motion.div
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-6 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all"
            animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <service.icon className="text-teal-300" size={32} />
          </motion.div>

          <h3 className="text-xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all">
            {service.title}
          </h3>

          <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
            {service.description}
          </p>

          <ul className="space-y-3">
            {service.features.map((feature, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
              >
                <ChevronRight size={14} className="text-emerald-400 mt-1 flex-shrink-0" />
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= SERVICES SECTION =============
const ServicesSection = () => {
  const services = [
    {
      icon: Image,
      title: 'Image Labeling',
      description: "We look at your pictures and mark what's in them. Like circling faces, drawing boxes around cars, or tagging different objects.",
      features: [
        'Draw boxes around objects',
        'Identify and name everything in photos',
        'Mark specific body parts or features',
        'Count items in images',
        'Tag image quality and conditions'
      ]
    },
    {
      icon: Video,
      title: 'Video Annotation',
      description: "We watch your videos frame-by-frame and mark what's happening. Track people moving, objects changing, or actions taking place.",
      features: [
        'Track objects as they move',
        'Identify actions and activities',
        'Mark key moments in videos',
        'Label facial expressions and emotions',
        'Note when things appear or disappear'
      ]
    },
    {
      icon: FileText,
      title: 'Text Labeling',
      description: "We read your text and categorize it. Mark if reviews are positive or negative, identify names and places, or classify documents.",
      features: [
        'Sort text into categories',
        'Mark names, dates, and locations',
        'Identify positive or negative opinions',
        'Find and label key information',
        'Tag language and writing style'
      ]
    },
    {
      icon: Mic,
      title: 'Audio Transcription',
      description: "We listen to your audio and write down what's said. Mark who's speaking, note background sounds, and timestamp important moments.",
      features: [
        'Convert speech to written text',
        'Identify different speakers',
        'Mark pauses and emotions in voice',
        'Note background noises',
        'Tag language and accents'
      ]
    },
    {
      icon: Map,
      title: 'Geospatial Tagging',
      description: "We mark locations on maps and satellite images. Identify buildings, roads, fields, or any features visible from above.",
      features: [
        'Label buildings and structures',
        'Mark roads and pathways',
        'Identify land types (forest, water, city)',
        'Tag infrastructure locations',
        'Measure distances and areas'
      ]
    },
    {
      icon: Tags,
      title: 'Product Categorization',
      description: "We sort and tag your products or inventory items. Add categories, descriptions, attributes, and tags so AI can organize items.",
      features: [
        'Categorize products correctly',
        'Add descriptive tags and attributes',
        'Identify product features',
        'Rate product quality',
        'Link related or similar items'
      ]
    }
  ];

  return (
    <section id="services" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Our Services
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Types of Labeling We Do
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            We label all kinds of information so AI can understand it. From pictures and videos 
            to text and audio—we've got you covered.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= LABEL TYPES SECTION =============
const LabelTypesSection = () => {
  const labelTypes = [
    {
      icon: Square,
      title: 'Bounding Boxes',
      description: "We draw rectangular boxes around objects in images. Like drawing a square around each car in a parking lot photo.",
      usedFor: 'Self-driving cars, security cameras, counting objects, product detection'
    },
    {
      icon: Scissors,
      title: 'Segmentation',
      description: "We trace the exact outline of objects—like cutting them out with scissors. More precise than boxes.",
      usedFor: 'Medical imaging, photo editing apps, fashion try-on, robotics vision'
    },
    {
      icon: Tag,
      title: 'Classification Tags',
      description: 'We add simple labels to entire items. Like stamping "cat" on a cat photo or "happy" on a smiling face.',
      usedFor: 'Social media filters, content moderation, email sorting, search engines'
    },
    {
      icon: MapPin,
      title: 'Keypoint Marking',
      description: "We mark specific points on objects—like dots on a face showing eyes, nose, and mouth locations.",
      usedFor: 'Face filters, body motion tracking, gesture recognition, AR effects'
    },
    {
      icon: Link2,
      title: 'Relationship Mapping',
      description: 'We connect things that relate to each other. Like linking "John" to "CEO" or connecting questions to answers.',
      usedFor: 'Chatbots, knowledge graphs, document understanding, recommendation systems'
    },
    {
      icon: Smile,
      title: 'Sentiment Tagging',
      description: "We read text or watch videos and mark the emotion or opinion. Label if something is happy, sad, or angry.",
      usedFor: 'Customer feedback analysis, social media monitoring, review sorting'
    }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Label Types
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Different Ways We Label
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Depending on what you need, we use different labeling techniques. Here are the 
            main methods explained in simple terms.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {labelTypes.map((type, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300 h-full flex flex-col"
                whileHover={{ y: -5 }}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-4 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <type.icon className="text-teal-300" size={28} />
                </div>
                <h3 className="text-lg font-bold text-teal-400 mb-3">
                  {type.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4 flex-grow">
                  {type.description}
                </p>
                
                <div className="bg-emerald-500/10 border-l-4 border-emerald-500 rounded-r-xl p-4">
                  <p className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-1">Used For:</p>
                  <p className="text-sm text-gray-300">
                    {type.usedFor}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= PROCESS SECTION =============
const ProcessSection = () => {
  const steps = [
    { number: 1, icon: Upload, title: 'You Send Us Data', description: 'Upload your images, videos, text, or audio files to our secure system. We accept any format and any amount.' },
    { number: 2, icon: MessageSquare, title: 'We Understand Your Needs', description: "Quick call or email to clarify what you want labeled. You show us examples and we make sure we're aligned." },
    { number: 3, icon: Users, title: 'Our Team Labels Everything', description: 'Trained humans carefully go through each item, adding accurate labels. Multiple people check each piece.' },
    { number: 4, icon: CheckCircle, title: 'Quality Check & Review', description: "Supervisors review samples to ensure accuracy. We fix mistakes before sending data back to you." },
    { number: 5, icon: Download, title: 'You Get Labeled Data', description: "Download your completed labeled dataset ready to train AI. We'll fix anything until you're 100% satisfied." }
  ];

  return (
    <section id="process" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Our Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How We Label Your Data
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Our simple 5-step process ensures your data gets labeled accurately and quickly.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300 h-full">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-2xl" />
                
                <motion.div
                  className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center mb-4 text-white font-bold text-xl"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  {step.number}
                </motion.div>
                <h3 className="text-lg font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= QUALITY SECTION =============
const QualitySection = () => {
  const qualityItems = [
    { icon: UserCheck, title: 'Trained Humans', description: "Real people, not robots. Our team understands context and nuance that automated systems miss." },
    { icon: Search, title: 'Multiple Reviews', description: 'Every item gets checked by 2-3 different people. Catches mistakes and ensures consistency.' },
    { icon: FileCheck, title: 'Clear Guidelines', description: "We create detailed instructions based on your needs. Everyone follows the same rules." },
    { icon: Target, title: '99%+ Accuracy', description: 'We guarantee accuracy above 99% or we redo the work for free.' },
    { icon: Lock, title: 'Data Security', description: 'Your data stays private and secure. Encrypted transfers and secure servers.' },
    { icon: Zap, title: 'Fast Turnaround', description: 'We work around the clock. Get your labeled data back in days, not weeks.' }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Quality Standards
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Why Our Labels Are Better
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Quality matters when teaching AI. Bad labels = bad AI that makes mistakes.
          </p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qualityItems.map((item, index) => (
              <motion.div
                key={index}
                className="group text-center p-6 bg-white/[0.03] border border-white/10 rounded-2xl hover:border-teal-500/30 transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-4 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <item.icon className="text-teal-300" size={28} />
                </div>
                <h3 className="text-lg font-bold text-teal-400 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= INDUSTRIES SECTION =============
const IndustriesSection = () => {
  const industries = [
    { icon: Car, name: 'Self-Driving Cars', use: 'Label road signs, pedestrians, vehicles, and obstacles' },
    { icon: Stethoscope, name: 'Healthcare', use: 'Mark medical images, label symptoms, categorize records' },
    { icon: ShoppingCart, name: 'E-Commerce', use: 'Tag products, categorize items, label customer reviews' },
    { icon: Landmark, name: 'Banking', use: 'Label transactions, categorize documents, process forms' },
    { icon: Factory, name: 'Manufacturing', use: 'Mark defects, label machine parts, identify quality issues' },
    { icon: Smartphone, name: 'Social Media', use: 'Identify content, tag faces, categorize posts, label emotions' },
    { icon: Wheat, name: 'Agriculture', use: 'Label crop health, identify pests, mark field boundaries' },
    { icon: Film, name: 'Entertainment', use: 'Tag video content, label scenes, categorize for recommendations' }
  ];

  return (
    <section id="industries" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Industries We Serve
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Who Needs Data Labeling?
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Almost every industry using AI needs labeled data. Here are some examples.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center hover:border-teal-500/30 transition-all duration-300 h-full"
                whileHover={{ y: -5 }}
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-4 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <industry.icon className="text-teal-300" size={28} />
                </div>
                <h3 className="text-base font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text mb-2">
                  {industry.name}
                </h3>
                <p className="text-gray-400 text-xs leading-relaxed">
                  {industry.use}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= PRICING SECTION =============
const PricingSection = ({ onSelectPlan }) => {
  const plans = [
    {
      tier: 'Basic Labels',
      price: '$0.10',
      period: 'Per Item',
      featured: false,
      planValue: 'Basic Labels ($0.10/item)',
      features: [
        'Simple classification tags',
        'Basic bounding boxes',
        'Text categorization',
        'Standard turnaround (5-7 days)',
        '99% accuracy guarantee',
        'Email support'
      ]
    },
    {
      tier: 'Advanced Labels',
      price: '$0.50',
      period: 'Per Item',
      featured: true,
      planValue: 'Advanced Labels ($0.50/item)',
      features: [
        'Detailed segmentation',
        'Keypoint annotation',
        'Video frame labeling',
        'Fast turnaround (2-3 days)',
        '99.5% accuracy guarantee',
        'Priority support',
        'Dedicated project manager'
      ]
    },
    {
      tier: 'Enterprise Volume',
      price: 'Custom',
      period: "Let's Talk",
      featured: false,
      planValue: 'Enterprise Volume (Custom)',
      features: [
        'Millions of items',
        'Custom labeling workflows',
        'Specialized training',
        'Rush delivery available',
        '99.9% accuracy guarantee',
        '24/7 dedicated team',
        'API integration',
        'Volume discounts'
      ]
    }
  ];

  return (
    <section id="pricing" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Pricing
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Simple Pricing
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Pay only for what you need. No hidden fees or surprises.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative group ${plan.featured ? 'md:-mt-4 md:mb-4' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="px-4 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full text-xs font-bold text-slate-900">
                    POPULAR CHOICE
                  </span>
                </div>
              )}

              <div className={`relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border rounded-3xl p-8 h-full flex flex-col transition-all duration-300 ${
                plan.featured 
                  ? 'border-teal-500/50 shadow-lg shadow-teal-500/20' 
                  : 'border-white/10 hover:border-teal-500/30'
              }`}>
                <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-3xl ${
                  plan.featured 
                    ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500' 
                    : 'bg-gradient-to-r from-blue-500/50 via-teal-500/50 to-emerald-500/50'
                }`} />

                <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">
                  {plan.tier}
                </h3>

                <div className="text-center mb-6">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
                    {plan.price}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {plan.period}
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400">
                      <Check size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onSelectPlan(plan.planValue)}
                  className={`w-full py-4 rounded-full font-semibold transition-all ${
                    plan.featured
                      ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white hover:shadow-lg hover:shadow-teal-500/50'
                      : 'border border-white/20 text-white hover:bg-white/10 hover:border-teal-500/50'
                  }`}
                >
                  {plan.tier === 'Enterprise Volume' ? 'Contact Us' : 'Get Started'}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onGetSample }) => {
  return (
    <section className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-4xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-teal-500/20 to-emerald-600/20 blur-3xl" />

        <motion.div
          className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-12 md:p-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Ready to Label Your Data?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Send us a sample of your data and we'll show you what labeled output looks like. 
            No commitment required—see our quality firsthand!
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onGetSample}
            className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-3 mx-auto"
          >
            Get Free Sample <ArrowRight size={20} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/10 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              High-quality data labeling services to train your AI systems with accuracy.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Services</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-engineer-augmentation" className="hover:text-teal-400 transition">Staff Augmentation</Link></li>
              <li><Link to="/ai-project-execution" className="hover:text-teal-400 transition">Project Execution</Link></li>
              <li><Link to="/custom-ai-ml-solutions" className="hover:text-teal-400 transition">Custom Solutions</Link></li>
              <li><Link to="/data-labelling-services" className="hover:text-teal-400 transition">Data Labelling</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-teal-400 transition">Training Programs</Link></li>
              <li><Link to="/services" className="hover:text-teal-400 transition">All Services</Link></li>
              <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Cookie Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function DataLabellingServicesPage() {
  const [sampleModal, setSampleModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleGetSample = (plan = null) => {
    setSelectedPlan(plan);
    setSampleModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onGetSample={() => handleGetSample()} />
      <Hero onGetSample={() => handleGetSample()} />
      <ExplainerSection />
      <ServicesSection />
      <LabelTypesSection />
      <ProcessSection />
      <QualitySection />
      <IndustriesSection />
      <PricingSection onSelectPlan={handleGetSample} />
      <FinalCTA onGetSample={() => handleGetSample()} />
      <Footer />

      {/* Sample Request Modal */}
      <SampleRequestModal
        isOpen={sampleModal}
        onClose={() => setSampleModal(false)}
        selectedPlan={selectedPlan}
      />
    </div>
  );
}